import React from "react";
import Head from "next/head";
import { Button, ConfigProvider, Layout } from "antd";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useRouter } from "next/router";
import { PopupTypes } from "./Vacancy/VacancyModel";
import encryptData from "@/functions/encryptInput";
import { useResendConfirmEmail } from "@/services/AuthenticationService";
import { useSession } from "next-auth/react";
import usePopupState from "./Vacancy/PopupState";
import Modal from "./Modal";

const sidebarBackgroundColor = "#93885B";

export interface NavItem {
  label: string;
  href: string;
}

const UserLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { route } = useRouter();

  const navbarItems: NavItem[] = [
    { label: "Career", href: "/" },
    { label: "Vacancy", href: "/vacancy" },
    { label: "Accelist Group", href: "#!" },
  ];

  const { data: session } = useSession();
  const isEmailVerified = session ? session["email_verified"] : false;
  const { popupState, setPopup } = usePopupState();
  const { resendConfirmEmail } = useResendConfirmEmail();
  
  const handleResendEmail = async () => {
      setPopup(PopupTypes.ShowErrorPopup, false);
      const email = session?.user?.email ?? "";
      console.log(email);
      if (email) {
          const encryptedEmail = await encryptData(email);
          await resendConfirmEmail(encryptedEmail);
      }
      setPopup(PopupTypes.ShowSuccessPopup, true);
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            // Sidebar background color:
            // https://github.com/ant-design/ant-design/blob/5.0.0/components/layout/style/index.tsx#L101
            colorBgHeader: sidebarBackgroundColor,
          },
          Menu: {
            colorText: "white",
            // https://github.com/ant-design/ant-design/blob/5.0.0/components/menu/style/theme.tsx#L194

            // https://github.com/ant-design/ant-design/blob/5.0.0/components/menu/style/theme.tsx#L133
            colorBgTextHover: "white",
          },
        },
        token: {
          // Seed Token
          colorPrimary: "#93885B",

          // Alias Token
          colorBgContainer: "#f6ffed",
        },
      }}
    >
      <Layout className="min-h-screen">
        <Head>
          <meta key="meta-charset" charSet="utf-8" />
          <meta
            key="meta-viewport"
            name="viewport"
            content="width=device-width, initial-scale=1"
          />
          <link key="favicon" rel="icon" href="/accelistLogo.ico" />
        </Head>
        <Layout className="bg-basic min-h-screen">
          <Navbar items={navbarItems} />

          {session && !isEmailVerified && <>
            <div className="text-center p-2 bg-[#93885B] text-white">
              Your account is not verified yet. Check your email to verify your account. Not receive an email?
              <Button type="link" className="px-1" onClick={handleResendEmail}>
                <span className="font-bold underline">
                    Click here
                </span>
              </Button>
            </div>
            <Modal
              open={popupState[PopupTypes.ShowSuccessPopup]}
              modalTitle="Success"
              imageUri="success-apply.svg"
            >
              <p className="mt-4 font-medium text-slate-500 text-base text-center max-w-sm">
                Verification email is sent successfully. Please check your email.
              </p>
              <Button 
                  onClick={() => setPopup(PopupTypes.ShowSuccessPopup, false)}
                  className="w-full my-8 h-fit py-2 text-center text-white bg-[#93885B] rounded-xl">
                  Ok
              </Button>
            </Modal>
          </>
          }
          {children}
          {route == "/changePassword" ? "" : <Footer />}
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export const WithUserLayout = (page: React.ReactElement) => (
  <UserLayout>{page}</UserLayout>
);
